import { createContext, use, useEffect, useMemo, useState, type PropsWithChildren } from 'react';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

type PwaInstallContextValue = {
  canInstall: boolean;
  install: () => Promise<boolean>;
  isInstalled: boolean;
};

const PwaInstallContext = createContext<PwaInstallContextValue | null>(null);

function isRunningStandalone() {
  const standaloneNavigator = navigator as Navigator & { standalone?: boolean };

  return window.matchMedia('(display-mode: standalone)').matches || standaloneNavigator.standalone === true;
}

export function PwaInstallProvider({ children }: PropsWithChildren) {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(() => isRunningStandalone());

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setPromptEvent(event as BeforeInstallPromptEvent);
    };
    const handleInstalled = () => {
      setPromptEvent(null);
      setIsInstalled(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const value = useMemo<PwaInstallContextValue>(
    () => ({
      canInstall: Boolean(promptEvent) && !isInstalled,
      isInstalled,
      install: async () => {
        if (!promptEvent) return false;

        await promptEvent.prompt();
        const choice = await promptEvent.userChoice.catch(() => null);
        setPromptEvent(null);
        return choice?.outcome === 'accepted';
      },
    }),
    [isInstalled, promptEvent],
  );

  return <PwaInstallContext.Provider value={value}>{children}</PwaInstallContext.Provider>;
}

// The hook intentionally lives with its provider so the install event state stays encapsulated.
// eslint-disable-next-line react-refresh/only-export-components
export function usePwaInstall() {
  const context = use(PwaInstallContext);

  if (!context) throw new Error('usePwaInstall must be used inside PwaInstallProvider');

  return context;
}
